import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useCaseFrameLeaveGuard } from "@/zapi-frontend/stores/caseFrameLeaveGuard";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import type { TNavTarget, TProjectPageView } from "../model/types";
import { chapterParams, homeParams, presentationParams, viewParams } from "../lib/projectViewParams";

export function useProjectViewNavigation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isPresentationOpen = searchParams.get("tab") === "presentation";
  const { viewMode, activeChapter, setActiveChapter, setViewMode } = useChapterStore();
  const { closeHandler } = useProjectSidebarStore();
  const { isDirty: isCaseFrameDirty, setPendingLeave: setCaseFramePendingLeave } = useCaseFrameLeaveGuard();

  const guardCaseFrameLeave = useCallback(
    (leave: () => void) => {
      if (viewMode === "projectFrame" && isCaseFrameDirty) {
        setCaseFramePendingLeave(leave);
        return;
      }
      leave();
    },
    [viewMode, isCaseFrameDirty, setCaseFramePendingLeave]
  );

  const openChapter = useCallback(
    (chapterId: number) => {
      if (viewMode === "chapter" && activeChapter?.id === chapterId && !isPresentationOpen) return;
      guardCaseFrameLeave(() => {
        setActiveChapter(chapterId.toString());
        setViewMode("chapter");
        setSearchParams((prev) => chapterParams(prev, chapterId));
      });
    },
    [
      viewMode,
      activeChapter?.id,
      isPresentationOpen,
      guardCaseFrameLeave,
      setActiveChapter,
      setViewMode,
      setSearchParams
    ]
  );

  const openView = useCallback(
    (view: TProjectPageView) => {
      const navigateToView = () => {
        closeHandler();
        setViewMode(view);
        setSearchParams((prev) => viewParams(prev, view));
      };
      if (view === "projectFrame") navigateToView();
      else guardCaseFrameLeave(navigateToView);
    },
    [closeHandler, setViewMode, setSearchParams, guardCaseFrameLeave]
  );

  const openHome = useCallback(() => {
    if ((viewMode === "dashboard" || viewMode === "projectOverview") && !isPresentationOpen) return;
    guardCaseFrameLeave(() => {
      closeHandler();
      setViewMode("projectOverview");
      setSearchParams(homeParams);
    });
  }, [viewMode, isPresentationOpen, guardCaseFrameLeave, closeHandler, setViewMode, setSearchParams]);

  const openPresentation = useCallback(() => setSearchParams(presentationParams()), [setSearchParams]);

  const navigate = useCallback(
    (target: TNavTarget) => {
      if (target.kind === "home") openHome();
      else if (target.kind === "chapter") openChapter(target.chapterId);
      else if (target.kind === "view") openView(target.view);
      else openPresentation();
    },
    [openHome, openChapter, openView, openPresentation]
  );

  return { navigate };
}
